import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { payCustomerOrder, clearPendingCheckout } from '../services/checkoutService';
import { customerOrderStatusLabel, getOrderItems, orderDate, orderGrandTotal } from '../utils/orderPresentation';
import PaymentChoice from './PaymentChoice';
import OrderProductList from './OrderProductList';
import { checkoutSelectionKey, isDifferentCartCheckout } from '../services/checkoutSelection';

export default function CheckoutRecovery({ pending, initialError, onResolved, checkoutSource, selectedItems = [] }) {
  const [order, setOrder] = useState(null), [error, setError] = useState(''), [loading, setLoading] = useState(true), [paying, setPaying] = useState(false);
  const activePayment = useRef(false);
  const [savedCardId, setSavedCardId] = useState('');
  const continuation = useRef({ onResolved, selectedItems });
  continuation.current = { onResolved, selectedItems };
  const selectionKey = checkoutSelectionKey(selectedItems);
  useEffect(() => {
    let active = true;
    if (!pending.orderId) { setLoading(false); return undefined; }
    setLoading(true); setError('');
    api.get(`/api/orders/customer/${pending.orderId}`).then(({ data }) => {
      if (!active) return;
      if (isDifferentCartCheckout(checkoutSource, pending, data, continuation.current.selectedItems)) {
        clearPendingCheckout(pending.orderId);
        continuation.current.onResolved({ id: data.id, status: data.status });
        return;
      }
      setOrder(data);
    })
      .catch(e => { if (active) setError(e.response?.data?.error || 'Не удалось загрузить созданный заказ. Откройте «Мои заказы».'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [pending.orderId, checkoutSource, selectionKey, pending]);
  const pay = async () => {
    if (!order || activePayment.current) return;
    activePayment.current = true; setPaying(true); setError('');
    try { window.location.assign(await payCustomerOrder(order.id, Math.round(orderGrandTotal(order) * 100), savedCardId)); }
    catch (e) { setError(e.response?.data?.error || e.message || 'Не удалось открыть банковскую форму. Проверьте статус оплаты.'); }
    finally { activePayment.current = false; setPaying(false); }
  };
  const reset = () => { clearPendingCheckout(order.id); onResolved(); };
  return <div className="checkout-container">
    <h1>Продолжить оформление</h1>
    {error && <p role="alert" className="checkout-notice checkout-notice--error">{error}</p>}
    {loading ? <p role="status">Загружаем сохранённый заказ…</p> : order ? <>
      {initialError && order.status === 'CREATED' && <p className="checkout-notice">Оформление было прервано до перехода в банк. Проверьте сохранённый итог перед оплатой.</p>}
      {Number.isSafeInteger(pending.expectedAmountMinor) && pending.expectedAmountMinor !== Math.round(orderGrandTotal(order) * 100) && <p role="status" className="checkout-notice">Цена изменилась. Ниже указан актуальный итог сохранённого заказа.</p>}
      <div className="checkout-card">
        <h2>Итог заказа</h2>
        <p>{orderDate(order)} · {customerOrderStatusLabel(order.status)}</p>
        <OrderProductList items={getOrderItems(order)} />
        <p>Товары: <strong>{order.totalAmount} ₽</strong></p>
        <p>Доставка: <strong>{order.deliveryCost || 0} ₽</strong></p>
        <p>Всего: <strong>{orderGrandTotal(order)} ₽</strong></p>
      </div>
      {order.status === 'CREATED' && <><PaymentChoice value={savedCardId} onChange={setSavedCardId} disabled={paying} /><button type="button" className="checkout-btn-primary" disabled={paying} onClick={pay}>{paying ? 'Проверяем оплату…' : `${savedCardId ? 'Оплатить' : 'Продолжить оплату'} ${orderGrandTotal(order)} ₽`}</button></>}
      {order.status === 'CANCELLED' && <button type="button" className="btn btn-outline" onClick={reset}>Вернуться к корзине</button>}
      <Link className="btn btn-outline" to={`/orders/${order.id}`}>Открыть заказ</Link>
      <Link className="btn btn-outline" to={`/checkout/success?orderId=${order.id}`}>Проверить статус оплаты</Link>
    </> : <p>Результат создания заказа пока неизвестен. Проверьте «Мои заказы», прежде чем оформлять новый.</p>}
    <Link className="btn btn-outline" to="/orders">Мои заказы</Link>
  </div>;
}
